import React, { useEffect, useRef } from 'react';
import { Button, Flex, Typography, Modal, ConfigProvider } from 'antd';
import { THEME } from '../../main';
import { DownloadOutlined } from '@ant-design/icons';
import { useLazyGenerateProductionTravelerReportQuery } from '../../redux/api/order-line-item';

const { Text } = Typography;

interface ProductionTravelerButtonProps {
    lineItemId: string; // ID of the line item for report generation
    isDisabled?: boolean;
}

export const ProductionTravelerButton: React.FC<
    ProductionTravelerButtonProps
> = ({ lineItemId, isDisabled = false }) => {
    const modalShownRef = useRef(false);
    const [genericError, setGenericError] = React.useState(false);

    const [
        triggerProductionTravelerReport,
        { data: travelerData, isLoading: isTravelerLoading },
    ] = useLazyGenerateProductionTravelerReportQuery();

    // Handles the PDF file download when `travelerData` becomes available.
    useEffect(() => {
        if (travelerData) {
            const url = URL.createObjectURL(travelerData);
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 60000);
        }
    }, [travelerData]);

    const showMismatchModal = (intake: any, outtake: any, onConfirm: () => void) => {
        if (modalShownRef.current) return;
        modalShownRef.current = true;
        Modal.confirm({
            centered: true,
            closable: true,
            title: 'Traveler Quantities Mismatch',
            icon: null,
            content: `Intake (${intake ?? '—'}) and Outtake (${outtake ?? '—'}) quantities do not match. Please reconcile quantities or confirm to proceed.`,
            onOk: () => {
                modalShownRef.current = false;
                onConfirm();
            },
            onCancel: () => { modalShownRef.current = false; },
            okText: 'Acknowledge and Download',

            footer: (_, { OkBtn, CancelBtn }) => (
                <ConfigProvider theme={THEME}>
                    <CancelBtn />
                    <OkBtn />
                </ConfigProvider>
            ),
        });
    };

    const parseAndHandleError = async (err: any) => {
        try {
            let data: any = err?.data || err?.error || err;
            if (data instanceof Blob) {
                const text = await data.text();
                try {
                    data = JSON.parse(text);
                } catch {
                    data = { message: text };
                }
            }
            if (data?.code === 'INTAKE_OUTTAKE_MISMATCH') {
                return showMismatchModal(data?.intake_total, data?.outtake_total, () => {
                    setGenericError(false);
                    triggerProductionTravelerReport({ id: lineItemId, force: true }).unwrap().catch(() => {
                        setGenericError(true);
                    });
                });
            }
        } catch { }
        setGenericError(true);
    };

    // Intentionally do not auto-handle travelerError here to avoid duplicate modals.

    const handleDownloadTravelerReport = async () => {
        setGenericError(false);
        try {
            await triggerProductionTravelerReport({ id: lineItemId }).unwrap();
        } catch (err: any) {
            await parseAndHandleError(err);
        }
    };

    return (
        <>
            <Flex vertical align="start">
                <Text>Production Traveler Report</Text>
                <Button
                    icon={<DownloadOutlined />}
                    type="primary"
                    onClick={handleDownloadTravelerReport}
                    loading={isTravelerLoading}
                    disabled={isDisabled}

                >
                    {isTravelerLoading ? 'Generating...' : 'Download'}
                </Button>
                {genericError && (
                    <Text type="danger" style={{ marginTop: 4 }}>
                        Error generating report
                    </Text>
                )}
            </Flex>
        </>
    );
};
